<template lang="pug">
    div
        .text-center(v-if="isLoading")
            v-progress-circular(
                :size='100'
                :width='10'
                color='var(--volie-blue)'
                indeterminate
                style="margin-top: 100px; margin-bottom: 100px;"
            )
        v-container(v-else-if="validGoals" style="width: 100%; padding: 10px;")
            v-row.align-center.justify-end
                v-col(cols="3" class="text-right")
                    v-autocomplete(
                        v-model="selectedRecurringType"
                        :items="recurringTypes"
                        item-text="label"
                        item-value="value"
                        dense
                        outlined
                        hide-details
                        style="max-width: 200px; margin-left: auto;"
                        @change="onChangeRecurringType()"
                        :disabled="recurringTypes.length === 0"
                    )

            v-row.align-center
                template(v-if="selectedRecurringType")
                    v-col(v-for="(goalsObject, index) in getSelectedGoals" :key="index" cols="12")
                        BarChart(
                            :goals="goalsObject.goals"
                            :goal-type="goalsObject.type"
                            style="margin-bottom: -70px;"
                        )

        v-alert(v-else outlined type='info' dense border='left' style="margin-top: 0px;")
            | No historical goal data found.



</template>

<script lang="ts">
    import Vue from 'vue';
    import VolieState  from "@/store";

    // Components
    import BarChart from '@/components/dashboards/live/tabs/goals/historical_chart.vue';

    // Data
    import { GetHistoricalGoals } from "@/data";

    export default Vue.extend({
        data() {
            return {
                dailyGoals: [],
                weeklyGoals: [],
                monthlyGoals: [],
                quarterlyGoals: [],
                yearlyGoals: [],
                historicalGoals: null,
                isLoading: false,
                selectedRecurringType: null,
                recurringTypes: [],
            }
        },
        methods: {
            getHistoricalGoals() {

                const options = {
                    ownable_id: this.ownableId,
                    ownable_type: this.ownableType,
                    recurring_type: this.selectedRecurringType,
                }
                this.isLoading = true;

                GetHistoricalGoals(options).then((response) => {
                    if (!!response?.Data?.historical_goals && response?.Error === null) {
                        this.historicalGoals = response.Data.historical_goals;
                        this.initRecurringTypeObjects();
                        this.setRecurringTypes();
                    }
                    this.isLoading = false;
                });
            },
            initRecurringTypeObjects() {
                if (!this.historicalGoals) {
                    return;
                }

                if (this.historicalGoals?.daily_goals?.goal_types?.length > 0) {
                    const daily_types = this.historicalGoals.daily_goals.goal_types;
                    const daily_goals = this.historicalGoals.daily_goals.goals;

                    if (daily_types.length === daily_goals.length) {
                        for (let i = 0; i < daily_types.length; i++) {
                            const dailyType = {
                                type: daily_types[i],
                                goals: daily_goals[i],
                            }
                            this.dailyGoals.push(dailyType);
                        }
                    }
                }

                if (this.historicalGoals?.weekly_goals?.goal_types?.length > 0) {
                    const weekly_types = this.historicalGoals.weekly_goals.goal_types;
                    const weekly_goals = this.historicalGoals.weekly_goals.goals;

                    if (weekly_types.length === weekly_goals.length) {
                        for (let i = 0; i < weekly_types.length; i++) {
                            const weeklyType = {
                                type: weekly_types[i],
                                goals: weekly_goals[i],
                            }
                            this.weeklyGoals.push(weeklyType);
                        }
                    }
                }

                if (this.historicalGoals?.monthly_goals?.goal_types?.length > 0) {
                    const monthly_types = this.historicalGoals.monthly_goals.goal_types;
                    const monthly_goals = this.historicalGoals.monthly_goals.goals;

                    if (monthly_types.length === monthly_goals.length) {
                        for (let i = 0; i < monthly_types.length; i++) {
                            const monthlyType = {
                                type: monthly_types[i],
                                goals: monthly_goals[i],
                            }
                            this.monthlyGoals.push(monthlyType);
                        }
                    }
                }

                if (this.historicalGoals?.quarterly_goals?.goal_types?.length > 0) {
                    const quarterly_types = this.historicalGoals.quarterly_goals.goal_types;
                    const quarterly_goals = this.historicalGoals.quarterly_goals.goals;

                    if (quarterly_types.length === quarterly_goals.length) {
                        for (let i = 0; i < quarterly_types.length; i++) {
                            const quarterlyType = {
                                type: quarterly_types[i],
                                goals: quarterly_goals[i],
                            }
                            this.quarterlyGoals.push(quarterlyType);
                        }
                    }
                }

                if (this.historicalGoals?.yearly_goals?.goal_types?.length > 0) {
                    const yearly_types = this.historicalGoals.yearly_goals.goal_types;
                    const yearly_goals = this.historicalGoals.yearly_goals.goals;

                    if (yearly_types.length === yearly_goals.length) {
                        for (let i = 0; i < yearly_types.length; i++) {
                            const yearlyType = {
                                type: yearly_types[i],
                                goals: yearly_goals[i],
                            }
                            this.yearlyGoals.push(yearlyType);
                        }
                    }
                }
            },
            onChangeRecurringType() {
                this.resetGoals();
                this.getHistoricalGoals();
            },
            resetGoals() {
                this.historicalGoals = null;
                this.dailyGoals = [];
                this.weeklyGoals = [];
                this.monthlyGoals = [];
                this.quarterlyGoals = [];
                this.yearlyGoals = [];
            },
            setRecurringTypes() {
                this.recurringTypes = [];
                if (this.dailyGoals.length > 0) {
                    this.recurringTypes.push({ label: 'Daily', value: 'daily' });
                }
                if (this.weeklyGoals.length > 0) {
                    this.recurringTypes.push({ label: 'Weekly', value: 'weekly' });
                }
                if (this.monthlyGoals.length > 0) {
                    this.recurringTypes.push({ label: 'Monthly', value: 'monthly' });
                }
                if (this.quarterlyGoals.length > 0) {
                    this.recurringTypes.push({ label: 'Quarterly', value: 'quarterly' });
                }
                if (this.yearlyGoals.length > 0) {
                    this.recurringTypes.push({ label: 'Yearly', value: 'yearly' });
                }

                // Set selected recurring type to first available option if it's not set
                if (!this.selectedRecurringType && this.recurringTypes.length > 0) {
                    this.selectedRecurringType = this.recurringTypes[0].value;
                }
            },
        },
        computed: {
            currentUser() {
                return VolieState.currentUser();
            },
            getSelectedGoals() {
                switch(this.selectedRecurringType) {
                    case 'daily':
                        return this.dailyGoals;
                    case 'weekly':
                        return this.weeklyGoals;
                    case 'monthly':
                        return this.monthlyGoals;
                    case 'quarterly':
                        return this.quarterlyGoals;
                    case 'yearly':
                        return this.yearlyGoals;
                    default:
                        return [];
                }
            },
            validDailyGoals() {
                return this.dailyGoals?.length > 0;
            },
            validGoals() {
                return this.validDailyGoals || this.validWeeklyGoals || this.validMonthlyGoals || this.validQuarterlyGoals || this.validYearlyGoals;
            },
            validMonthlyGoals() {
                return this.monthlyGoals?.length > 0;
            },
            validQuarterlyGoals() {
                return this.quarterlyGoals?.length > 0;
            },
            validWeeklyGoals() {
                return this.weeklyGoals?.length > 0;
            },
            validYearlyGoals() {
                return this.yearlyGoals?.length > 0;
            },
        },
        components: {
            BarChart,
        },
        props: {
            forceRecurringType: String,
            ownableId: Number,
            ownableType: String,
            sheetHeight: Number,
        },
        watch: {
            sheetHeight() {
                this.handleWindowResize();
            },
            ownableId() {
                this.resetGoals();
                this.getHistoricalGoals();
            },
        },
        created() {
            if (!!this.forceRecurringType) {
                this.selectedRecurringType = this.forceRecurringType;
            }
            this.getHistoricalGoals();
        },
    });
</script>

<style lang="scss" scoped>
</style>
